import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import preProductionService from '../services/preProductionService';
import { Save, Settings, Ruler, Package, Clock, RotateCcw, Plus, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

// Categorias de tamanho de projeto
const PROJECT_SIZES = [
  { value: 'PEQUENO', label: 'Pequeno', description: ' até 10m² ou até 5 ambientes' },
  { value: 'MEDIO', label: 'Médio', description: '10-30m² ou 5-10 ambientes' },
  { value: 'GRANDE', label: 'Grande', description: '30-50m² ou 10-15 ambientes' },
  { value: 'EXTRA_GRANDE', label: 'Extra Grande', description: 'acima de 50m² ou mais de 15 ambientes' }
];

const PreProductionConfig = () => {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('measurement');
  const [configs, setConfigs] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Buscar configurações
  const { data: configData, isLoading } = useQuery({
    queryKey: ['preproduction-config'],
    queryFn: () => preProductionService.getConfig()
  });

  // Carregar configurações existentes
  useEffect(() => {
    if (configData?.data) {
      const configMap = {};
      configData.data.forEach(cfg => {
        configMap[cfg.config_key] = cfg.config_value;
      });
      setConfigs(configMap);
    }
  }, [configData]);

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: async (configKey, value, description) => {
      return preProductionService.updateConfig({
        config_key: configKey,
        config_value: value,
        description
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['preproduction-config']);
      toast.success('Configuração salva!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  });

  const handleSave = async (configKey, value, description) => {
    await saveMutation.mutateAsync(configKey, parseInt(value), description);
  };

  // Gerar configurações baseadas no tamanho do projeto
  const generateConfigs = () => {
    const newConfigs = [];
    PROJECT_SIZES.forEach(size => {
      // Conference de medidas
      newConfigs.push({
        key: `measurement_check_days_before_${size.value}`,
        label: `Conference de Medidas - ${size.label}`,
        value: configs[`measurement_check_days_before_${size.value}`] || configs['measurement_check_days_before'] || 7,
        description: `Dias antes da produção para ${size.label.toLowerCase()}`,
        size: size.value
      });

      // Dias após plano de corte para pedido
      newConfigs.push({
        key: `material_order_days_after_cut_${size.value}`,
        label: `Pedido de Materiais - ${size.label}`,
        value: configs[`material_order_days_after_cut_${size.value}`] || configs['material_order_days_after_cut'] || 3,
        description: `Dias após plano de corte para ${size.label.toLowerCase()}`,
        size: size.value
      });

      // Dias entre conference e plano de corte
      newConfigs.push({
        key: `cut_plan_days_after_measurement_${size.value}`,
        label: `Plano de Corte - ${size.label}`,
        value: configs[`cut_plan_days_after_measurement_${size.value}`] || configs['cut_plan_days_after_measurement'] || 2,
        description: `Dias após conference para plano de corte ${size.label.toLowerCase()}`,
        size: size.value
      });
    });

    // Adicionar padrões gerais
    newConfigs.push({
      key: 'measurement_check_days_before',
      label: 'Conference de Medidas (Padrão)',
      value: configs['measurement_check_days_before'] || 7,
      description: 'Dias antes da produção (padrão se não específico por tamanho)',
      isDefault: true
    });

    newConfigs.push({
      key: 'material_order_days_after_cut',
      label: 'Pedido de Materiais (Padrão)',
      value: configs['material_order_days_after_cut'] || 3,
      description: 'Dias após plano de corte (padrão se não específico por tamanho)',
      isDefault: true
    });

    newConfigs.push({
      key: 'cut_plan_days_after_measurement',
      label: 'Plano de Corte (Padrão)',
      value: configs['cut_plan_days_after_measurement'] || 2,
      description: 'Dias após conference (padrão se não específico por tamanho)',
      isDefault: true
    });

    return newConfigs;
  };

  const allConfigs = generateConfigs();

  const defaultConfigs = allConfigs.filter(c => c.isDefault);
  const sizeConfigs = allConfigs.filter(c => !c.isDefault);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Configurações de Pré-Produção
        </h1>
        <p className="text-gray-600 mt-1">
          Configure os prazos do fluxo de pré-produção. Você pode definir valores padrão ou específicos por tamanho de projeto.
        </p>
      </div>

      {/* Tabs de seção */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveSection('measurement')}
          className={`px-4 py-2 font-medium ${activeSection === 'measurement' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
        >
          <Ruler className="w-4 h-4 inline mr-2" />
          Conference de Medidas
        </button>
        <button
          onClick={() => setActiveSection('cut')}
          className={`px-4 py-2 font-medium ${activeSection === 'cut' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Plano de Corte
        </button>
        <button
          onClick={() => setActiveSection('orders')}
          className={`px-4 py-2 font-medium ${activeSection === 'orders' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Pedido de Materiais
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Configurações Padrão */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3">Valores Padrão</h3>
            <p className="text-sm text-blue-700 mb-4">
              Estes valores são usados quando não há configuração específica para o tamanho do projeto.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {defaultConfigs.map(config => (
                <ConfigInput
                  key={config.key}
                  config={config}
                  onSave={handleSave}
                  section={activeSection}
                />
              ))}
            </div>
          </div>

          {/* Configurações por Tamanho */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Valores por Tamanho de Projeto</h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure prazos específicos para cada tamanho de projeto. O sistema usará o valor mais específico disponível.
            </p>

            <div className="space-y-4">
              {PROJECT_SIZES.map(size => (
                <div key={size.value} className="border border-gray-300 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{size.label}</h4>
                      <p className="text-xs text-gray-500">{size.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {sizeConfigs
                      .filter(c => {
                        if (!c || !c.key) return false;
                        if (c.size !== size.value) return false;
                        const key = c.key.toLowerCase();
                        if (key.includes('measurement')) return activeSection === 'measurement';
                        if (key.includes('cut_plan')) return activeSection === 'cut';
                        if (key.includes('material_order')) return activeSection === 'orders';
                        return true;
                      })
                      .map(config => (
                        <ConfigInput
                          key={config.key}
                          config={config}
                          onSave={handleSave}
                          section={activeSection}
                          compact
                        />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente de input de configuração
const ConfigInput = ({ config, onSave, section, compact = false }) => {
  const [value, setValue] = useState(config.value);
  const [isEditing, setIsEditing] = useState(false);

  // Verificar se esta configuração pertence à seção atual
  const sectionMatch = () => {
    if (config.key.includes('measurement')) return section === 'measurement';
    if (config.key.includes('cut_plan')) return section === 'cut';
    if (config.key.includes('material_order')) return section === 'orders';
    return true;
  };

  if (!sectionMatch()) return null;

  const handleSave = () => {
    onSave(config.key, value, config.description);
    setIsEditing(false);
  };

  return (
    <div className={compact ? '' : 'flex-1'}>
      <label className="block text-xs text-gray-600 mb-1">
        {config.label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          max="60"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsEditing(true);
          }}
          className="w-16 border rounded px-2 py-1 text-sm"
        />
        <span className="text-xs text-gray-500">dias</span>
        {isEditing && (
          <button
            onClick={handleSave}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
            title="Salvar"
          >
            <Save className="w-4 h-4" />
          </button>
        )}
      </div>
      {!compact && (
        <p className="text-xs text-gray-400 mt-1">{config.description}</p>
      )}
    </div>
  );
};

export default PreProductionConfig;